import { useEffect, useState } from 'react';
import type { PreviewError } from '~/lib/stores/previews';
import { classNames } from '~/utils/classNames'; // Assuming you have a utility like this
import { ERROR_LIST, getCombinedErrorTextForCopy, groupAndCountErrors, type GroupedError } from '~/utils/errorUtils';
interface ErrorCardProps {
  error: PreviewError[] | null;
  onClose: () => void;
  onFixError: (error: PreviewError[]) => void;
  isStreaming?: boolean;
  className?: string; // Allow passing custom classes for positioning
}

const ErrorItem = ({ groupedError, isFixing }: { groupedError: GroupedError; isFixing: boolean }) => {
  const [isStackVisible, setIsStackVisible] = useState(false);
  const { representativeError, count } = groupedError;

  const toggleStackTrace = () => {
    setIsStackVisible((prev) => !prev);
  };

  const highlightFilePaths = (stack: string) => {
    if (!stack) {
      return '';
    }

    return stack.split('\n').map((line, index) => {
      const filePathMatch = line.match(/\(\/([^)]+)\)/);

      if (filePathMatch) {
        const beforePath = line.substring(0, line.indexOf('(/'));
        const filePath = filePathMatch[1];
        const afterPath = line.substring(line.indexOf('(/') + filePathMatch[0].length);
        return (
          <div key={index} className="stack-trace-line">
            {beforePath}
            <span className="text-yellow-400">(/{filePath}</span>
            {afterPath}
          </div>
        );
      }

      return (
        <div key={index} className="stack-trace-line">
          {line}
        </div>
      );
    });
  };

  return (
    <div className="border-b border-white/10 last:border-b-0 py-3 first:pt-0 last:pb-0">
      <div className="text-sm font-medium mb-1 text-[#e7e7e7] mt-2">
        {count > 1 && (
          <span className="text-[10px] border border-white  text-white rounded-full px-1 mr-2 leading-relaxed ">
            {count}
          </span>
        )}
        {representativeError.message}
      </div>
      {representativeError.stack && (
        <button
          onClick={toggleStackTrace}
          disabled={isFixing}
          className="flex items-center gap-1 text-xs text-[#e7e7e7]/70 hover:text-[#e7e7e7] bg-transparent border-none p-0 mb-2 cursor-pointer disabled:opacity-50"
        >
          <span>{isStackVisible ? 'Hide Details' : 'Show Details'}</span>
          <div
            className={classNames(
              'i-ph:caret-down-bold w-3 h-3 transition-transform',
              isStackVisible ? 'rotate-180' : '',
            )}
          />
        </button>
      )}
      {isStackVisible && representativeError.stack && (
        <div className="mb-2">
          <pre className="text-xs bg-[#2c2c2c] p-3 rounded overflow-auto max-h-[150px] text-[#e7e7e7]/70 border border-white/10">
            {highlightFilePaths(representativeError.stack)}
          </pre>
        </div>
      )}
    </div>
  );
};

export const ErrorCard = ({
  error: errorArray,
  onClose,
  onFixError,
  isStreaming = false,
  className = '',
}: ErrorCardProps) => {
  const [isFixRequested, setIsFixRequested] = useState(false);
  const [isErrorListExpandedWhileStreaming, setIsErrorListExpandedWhileStreaming] = useState(false); // New state

  const groupedErrors = groupAndCountErrors(errorArray);

  // Reset fix requested state when streaming ends and close
  useEffect(() => {
    if (!isStreaming && isFixRequested) {
      console.log('Streaming ended, closing card...'); // Debugging line

      const timer = setTimeout(() => {
        console.log('Fix requested, closing card...'); // Debugging line
        setIsFixRequested(false);
        onClose(); // Auto-close card when fix is complete and stream ends
      }, 1); // Keep the delay for user feedback

      return () => clearTimeout(timer);
    }

    if (!errorArray || errorArray.length === 0) {
      setIsFixRequested(false);
    }

    return undefined;
  }, [isStreaming, onClose, errorArray]);

  if (groupedErrors.length === 0) {
    return null;
  }

  const handleFixAllErrors = () => {
    if (errorArray && errorArray.length > 0) {
      setIsFixRequested(true);
      onFixError(errorArray); // Pass the whole array
    }
  };

  const isFixing = isFixRequested || isStreaming;
  const totalOriginalErrors = errorArray?.length || 0;

  const combinedText = getCombinedErrorTextForCopy(groupedErrors);

  const errorType = ERROR_LIST.find((error) => combinedText.includes(error)) || 'Preview Error';

  return (
    <div
      className={classNames(
        isStreaming ? 'fixed top-[76px] w-[368px]' : 'sticky bottom-[150px] mt-3 w-full',
        'bg-alpha-background-secondary rounded-xl border border-white/10 shadow-lg max-w-[480px]', // Increased max-width for multiple errors
        'overflow-hidden',
        className,
        'transition-transform duration-[3000] ease-in-out',
      )}
      id="error-card"
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-2">
          <div className="flex items-center gap-2 text-red-500">
            <div className="i-ph:warning-circle-bold text-lg shrink-0" />
            <h3 className="text-base font-semibold text-red-500 m-0">{errorType}</h3>
          </div>
          {isStreaming && (
            <div className="flex items-center gap-1">
              <div className="text-[#e7e7e7] i-svg-spinners:90-ring-with-bg w-4 h-4 mr-1" />
              <span className="text-sm text-[#e7e7e7]">Generating Fix...</span>
            </div>
          )}
        </div>

        {/* Content Section: Changes based on isStreaming */}
        {isStreaming ? (
          // Content when STREAMING
          groupedErrors.length > 0 && (
            <div className="mt-2">
              <button
                onClick={() => setIsErrorListExpandedWhileStreaming((prev) => !prev)}
                className="flex items-center justify-between w-full text-sm text-[#e7e7e7]/80 hover:text-[#e7e7e7] bg-white/5 hover:bg-white/10 px-3 py-2 rounded mb-2 transition-colors"
              >
                <span>
                  {isErrorListExpandedWhileStreaming
                    ? 'Hide Errors'
                    : `Show ${groupedErrors.length} Error${groupedErrors.length !== 1 ? 's' : ''}`}
                </span>
                <div
                  className={classNames(
                    'i-ph:caret-down-bold w-4 h-4 transition-transform',
                    isErrorListExpandedWhileStreaming ? 'rotate-180' : '',
                  )}
                />
              </button>
              {isErrorListExpandedWhileStreaming && (
                <div className="max-h-[150px] overflow-y-auto pr-2 flex flex-col gap-2 border-t border-white/10 pt-2">
                  {groupedErrors.map((group, index) => (
                    <ErrorItem key={index} groupedError={group} isFixing={isFixing} />
                  ))}
                </div>
              )}
            </div>
          )
        ) : (
          // Content when NOT STREAMING
          <>
            <div className="h-px bg-[#414142] my-3" /> {/* Separator */}
            <div className="max-h-[300px] overflow-y-auto pr-2 mb-3 flex flex-col gap-2">
              {' '}
              {/* Error List */}
              {groupedErrors.map((group, index) => (
                <ErrorItem key={index} groupedError={group} isFixing={isFixing} />
              ))}
            </div>
            <div className="flex justify-between items-center pt-4 border-t border-white/10">
              {' '}
              {/* Action Buttons */}
              <button
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg hover:bg-white/10 text-[#e7e7e7] text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={onClose}
                disabled={isFixing}
              >
                Fixed Manually
              </button>
              <button
                onClick={handleFixAllErrors}
                disabled={isFixing || !errorArray || errorArray.length === 0}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-b from-alpha-primary-start to-alpha-primary-end rounded-lg hover:brightness-110 text-white text-sm font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isFixing && !isStreaming ? ( // Show "Fixing..." only if not streaming (streaming has "Generating Fix...")
                  <>
                    <div className="i-svg-spinners:90-ring-with-bg w-4 h-4" />
                    Fixing...
                  </>
                ) : (
                  <>
                    <div className="i-ph:wrench-light w-4 h-4" />
                    Fix All Errors
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
